import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { IdentificationCard, Crown, DownloadSimple, ArrowsClockwise, Copy, Image as ImageIcon } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

const TYPES = {
  explorer: { label: "Explorer Card", icon: IdentificationCard, formats: ["landscape", "square"] },
  prestige: { label: "Prestige Card", icon: Crown, formats: ["landscape", "square", "story"] },
};
const FMT_LABEL = { landscape: "Landscape 1200×630", square: "Square 1080×1080", story: "Story 1080×1920" };

export default function Cards() {
  const [type, setType] = useState("explorer");
  const [fmt, setFmt] = useState("landscape");
  const [card, setCard] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [shareText, setShareText] = useState("");
  const [busy, setBusy] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const loadImage = useCallback(async (opaqueId) => {
    try {
      const res = await api.get(`/cards/img/${opaqueId}`, { responseType: "blob" });
      setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setBusy(true); setDisabled(false); setImgUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
    try {
      const { data } = await api.get(`/cards/${type}`, { params: { fmt } });
      setCard(data);
      await loadImage(data.opaque_id);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  }, [type, fmt, loadImage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/cards/${type}/share-text`).then((r) => setShareText(r.data.text)).catch(() => {});
  }, [type]);

  const switchType = (t) => { setType(t); setFmt(TYPES[t].formats[0]); };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${type}/regenerate`, null, { params: { fmt } });
      setCard(data); await loadImage(data.opaque_id); toast.success("Card regenerated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl; a.download = `aurumward-${type}-${fmt}.png`; a.click();
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success("Share text copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const HeaderIcon = TYPES[type].icon;

  return (
    <div className="space-y-6" data-testid="cards-page">
      <div className="flex items-center gap-2">
        <HeaderIcon size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Shareable Cards</h1>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cards-type-selector">
        {Object.entries(TYPES).map(([v, cfg]) => (
          <button key={v} onClick={() => switchType(v)} data-testid={`card-type-${v}`}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors ${type === v ? "bg-ff-gold text-ff-bg" : "border border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>
            <cfg.icon size={16} weight="duotone" /> {cfg.label}
          </button>
        ))}
      </div>

      {disabled ? (
        <div className="ff-panel rounded-md p-10 text-center" data-testid="cards-disabled">
          <p className="text-ff-muted">Shareable cards are turned off in your privacy settings.</p>
          <a href="/settings" className="mt-3 inline-block rounded-sm bg-ff-gold px-4 py-2 font-semibold text-ff-bg hover:bg-ff-brass">Open Settings</a>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="cards-format-selector">
            {TYPES[type].formats.map((v) => (
              <button key={v} onClick={() => setFmt(v)} data-testid={`card-fmt-${v}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${fmt === v ? "bg-ff-gold text-ff-bg" : "border border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>
                {FMT_LABEL[v]}
              </button>
            ))}
          </div>

          <div className="ff-panel overflow-hidden rounded-md p-4">
            {busy && !imgUrl ? (
              <div className="flex h-64 items-center justify-center text-ff-muted">Rendering your card…</div>
            ) : imgUrl ? (
              <img src={imgUrl} alt={`Your ${TYPES[type].label}`} data-testid="card-preview" className="mx-auto max-h-[560px] w-auto max-w-full rounded-sm" />
            ) : (
              <div className="flex h-64 items-center justify-center text-ff-muted"><ImageIcon size={32} /></div>
            )}
            {card?.stale && <p className="mt-2 text-center text-xs text-ff-brass" data-testid="card-stale">Your stats changed — regenerate to refresh this card.</p>}
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={download} disabled={!imgUrl} data-testid="card-download-btn" className="flex items-center gap-1.5 rounded-sm bg-ff-gold px-4 py-2.5 font-semibold text-ff-bg hover:bg-ff-brass disabled:opacity-40"><DownloadSimple size={18} weight="bold" /> Download PNG</button>
            <button onClick={regenerate} disabled={busy} data-testid="card-regen-btn" className="flex items-center gap-1.5 rounded-sm border border-ff-brass/40 px-4 py-2.5 font-semibold text-ff-parchment hover:border-ff-gold hover:text-ff-gold disabled:opacity-40"><ArrowsClockwise size={18} /> Regenerate</button>
          </div>

          <div className="ff-panel rounded-md p-4" data-testid="cards-sharetext">
            <label className="text-xs uppercase tracking-widest text-ff-brass">Suggested share text (editable)</label>
            <textarea value={shareText} onChange={(e) => setShareText(e.target.value)} data-testid="card-sharetext-input" rows={2}
              className="mt-2 w-full rounded-sm border border-ff-brass/25 bg-ff-bg px-3 py-2 text-sm text-ff-parchment focus:border-ff-gold focus:outline-none" />
            <button onClick={copyShare} data-testid="card-copy-btn" className="mt-2 flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:text-ff-gold"><Copy size={16} /> Copy share text</button>
          </div>
        </>
      )}
    </div>
  );
}
